import { useRef, useState, type ReactNode } from "react";
import { bringIntoView } from "../../ui/scroll";
import type { Authorization, Order, Permission } from "../../api/types";
import { t } from "../../i18n";
import { OrdersByDay } from "../orders/OrdersByDay";
import type { OrdersState } from "../orders/useOrders";
import { MenuScreen } from "../menu-admin/MenuScreen";
import { FloorScreen } from "../floor-admin/FloorScreen";
import { GeneralScreen } from "../general/GeneralScreen";
import { PrintersScreen } from "../printing/PrintersScreen";
import { ZonesScreen } from "../zones-admin/ZonesScreen";
import { CustomersScreen } from "../customers-admin/CustomersScreen";
import { ReportsScreen } from "../reports/ReportsScreen";
import { TimeScreen } from "../time/TimeScreen";
import { StaffScreen } from "../staff-admin/StaffScreen";
import { SubscriptionScreen } from "../subscription/SubscriptionScreen";
import { PromotionsScreen } from "./PromotionsScreen";
import { SoldOutScreen } from "./SoldOutScreen";

/** A section of Beheer and who may open it (the same rights the server checks for every change). */
interface Section {
  id: string;
  name: string;
  /** The right needed, or "owner": only the owner (prototype "ownerOnly"). */
  right: Permission | "owner";
  render: () => ReactNode;
}

interface Props {
  /** Who opened Beheer with their code; the sections follow this person's rights. */
  authorization: Authorization;
  orders: OrdersState;
  onEdit: (order: Order, authorization: Authorization | null) => void;
  onClose: () => void;
}

/**
 * Beheer on the till, as in the prototype: opened with a code (owner or manager), a list of the
 * sections this person may use on the left, the section on the right, "✕ Beheer sluiten" below.
 * Sections are added here one by one as they are built; none is shown before it works.
 */
export function BeheerScreen({ authorization, orders, onEdit, onClose }: Props) {
  const who = authorization.staff;
  const owner = who.role === "owner";
  // in the order of the prototype; sections that are not built yet are not shown
  const sections: Section[] = [
    {
      id: "menu",
      name: t.beheer.sections.menu,
      right: "menu.edit",
      render: () => <MenuScreen authorization={authorization} />,
    },
    {
      id: "soldOut",
      name: t.beheer.sections.soldOut,
      right: "menu.soldout",
      render: () => <SoldOutScreen authorization={authorization} />,
    },
    {
      id: "promotions",
      name: t.beheer.sections.promotions,
      right: "menu.edit",
      render: () => <PromotionsScreen authorization={authorization} />,
    },
    {
      id: "staff",
      name: t.beheer.sections.staff,
      right: "staff.manage",
      render: () => <StaffScreen authorization={authorization} />,
    },
    {
      id: "printers",
      name: t.beheer.sections.printers,
      right: "settings.edit",
      render: () => <PrintersScreen authorization={authorization} />,
    },
    {
      id: "floor",
      name: t.beheer.sections.floor,
      right: "settings.edit",
      render: () => <FloorScreen authorization={authorization} />,
    },
    {
      id: "zones",
      name: t.beheer.sections.zones,
      right: "settings.edit",
      render: () => <ZonesScreen authorization={authorization} />,
    },
    {
      id: "customers",
      name: t.beheer.sections.customers,
      right: "settings.edit",
      render: () => <CustomersScreen authorization={authorization} />,
    },
    {
      id: "reports",
      name: t.beheer.sections.reports,
      right: "reports.view",
      render: () => <ReportsScreen authorization={authorization} />,
    },
    {
      id: "time",
      name: t.beheer.sections.time,
      right: "settings.edit",
      render: () => <TimeScreen authorization={authorization} />,
    },
    // no Meldingen here: only Tafo sees them, per shop in Tafo-beheer (Furkan, 15/10/2026: "zaak zelf niet")
    {
      id: "orders",
      name: t.beheer.sections.orders,
      right: "orders.delete",
      render: () => (
        <OrdersByDay
          live={orders}
          authorization={authorization}
          onEdit={onEdit}
          ownerBeheer={who.role === "owner" ? authorization : undefined}
        />
      ),
    },
    {
      id: "subscription",
      name: t.beheer.sections.subscription,
      right: "owner",
      render: () => <SubscriptionScreen authorization={authorization} />,
    },
    {
      id: "general",
      name: t.beheer.sections.general,
      right: "settings.edit",
      render: () => <GeneralScreen authorization={authorization} />,
    },
  ];
  const allowed = sections.filter((section) => (section.right === "owner" ? owner : who.can.includes(section.right)));
  const [chosen, setChosen] = useState(() => allowed[0]?.id ?? "");
  const current = allowed.find((section) => section.id === chosen) ?? allowed[0];
  /**
   * On a phone the list of sections is folded into one bar with the section open now (Furkan,
   * 10/10/2026: "open en dicht klappen"); a tap unfolds it, choosing folds it again. A wide screen keeps
   * the list at the left and never shows the bar.
   */
  const [folded, setFolded] = useState(true);
  const nav = useRef<HTMLElement>(null);

  return (
    <div className="admin">
      <nav ref={nav} className={`anav panel${folded ? " folded" : ""}`} aria-label={t.beheer.title}>
        <h2>{t.beheer.title}</h2>
        <button
          type="button"
          className="anav-fold"
          aria-expanded={!folded}
          aria-label={t.beheer.sectionsOf(current?.name ?? t.beheer.title)}
          onClick={() => {
            setFolded(!folded);
          }}
        >
          <span aria-hidden="true">☰</span>
          <span className="anav-now">{current?.name ?? t.beheer.title}</span>
          <span aria-hidden="true">{folded ? "▾" : "▴"}</span>
        </button>
        {allowed.map((section) => (
          <button
            key={section.id}
            type="button"
            aria-pressed={section.id === current?.id}
            onClick={() => {
              setChosen(section.id);
              setFolded(true);
              // folded on a phone: the chosen section starts right under the bar, the bar in sight
              if (typeof window.matchMedia === "function" && window.matchMedia("(max-width: 860px)").matches) {
                requestAnimationFrame(() => {
                  if (nav.current) bringIntoView(nav.current, { block: "start" });
                });
              }
            }}
          >
            {section.name}
          </button>
        ))}
        {allowed.length === 0 && <p className="note">{t.beheer.nothingYet}</p>}
        <p className="note">
          {t.beheer.signedIn(who.name, t.roles[who.role])}{" "}
          {who.role === "owner" ? t.beheer.ownerNote : t.beheer.otherNote}
        </p>
        <button type="button" className="btn close-admin" onClick={onClose}>
          {t.beheer.close}
        </button>
      </nav>
      <section className="admin-body" aria-label={current?.name ?? t.beheer.title}>
        {current?.render()}
      </section>
    </div>
  );
}
